import React from 'react';
import { useLocation } from '@reach/router';
import { graphql, useStaticQuery } from 'gatsby';
import { GatsbySeo } from 'gatsby-plugin-next-seo';
import { DAYE_LOGO_URL, SITE_ICON_URL } from '@constants';
import { SEOProps } from '@contracts';
import { useCountry } from '@hooks/useCountry';
import { replaceText } from '@utils/handleTextReplacement';
import { generateHreflangTags, generateProductSchema } from '@utils/seoTags';

const query = graphql`
  query SEO {
    site {
      siteMetadata {
        defaultTitle: title
        titleTemplate
        defaultDescription: description
        defaultImage: image
        siteUrl: siteUrl
        baseUrl: baseUrl
      }
    }
    allProductPriceData {
      edges {
        node {
          pricePerPlan {
            planType
            price {
              amount
              currency
            }
          }
          price {
            currency
            amount
          }
          vat {
            GB
          }
          name
          id__normalized
        }
      }
    }
  }
`;

interface Props {
  SEO: SEOProps;
}

const SEOTags = ({ SEO }: Props) => {
  const { uid, description, image, title, href, productData, articleData, manualHreflangs, noIndex, canonicalUrl } = SEO;
  const location = useLocation();
  const { country } = useCountry();
  const { site, allProductPriceData } = useStaticQuery(query);
  const { defaultTitle, defaultDescription, defaultImage, siteUrl, baseUrl } = site.siteMetadata;
  const productPriceData = allProductPriceData?.edges.map((edge) => edge.node).filter((node) => node.name !== null);
  const seoTitle = replaceText(title, country, location);
  const seo = {
    title: seoTitle || defaultTitle,
    description: description || defaultDescription,
    openGraph: {
      images: [{ url: image || defaultImage }],
      url: href || location.pathname,
      site_name: 'Daye',
      type: 'website',
      title: seoTitle || defaultTitle,
      description: description || defaultDescription
    },
    twitter: {
      handle: '@yourdaye',
      cardType: 'summary_large_image'
    }
  };

  // No need to handle the noindex logic within the GatsbySeo component as it's handled internally.
  // However, I'll keep the function here for any other logic you might add later.

  const isProduction = process.env.GATSBY_ACTIVE_ENV === 'prod';
  const noIndexTitleIncludes = ['Cannabinoid', 'Animal Testing'];
  const noIndexPathIncludes = ['/ppc-questionnaire', '/dashboard'];
  const noIndexPageEndsWith = ['/ads', '/ads/'];

  const handleNoIndex = () => {
    // If Prismic explicitly sets no_index, respect that setting
    if (noIndex !== undefined) {
      return noIndex;
    }

    // Otherwise, use existing logic
    if (!isProduction) return true;

    if (seo.title && noIndexTitleIncludes.some((substring: string) => seo.title.includes(substring))) {
      return true;
    }

    if (noIndexPathIncludes.some((substring: string) => seo.openGraph.url?.includes(substring))) {
      return true;
    }

    if (noIndexPageEndsWith.some((ending: string) => seo.openGraph.url?.endsWith(ending))) {
      return true;
    }

    return false;
  };

  const renderProductData = () => {
    const schemaAsString = generateProductSchema(productData, productPriceData, seoTitle, description ?? '', uid);
    return schemaAsString ? <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: schemaAsString }} /> : null;
  };

  const renderArticleData = () => {
    const { meta_description, title: articleTitle, category, author, featured_image } = articleData;

    const schema = {
      '@context': 'https://schema.org',
      '@type': 'Article',
      name: replaceText(articleTitle, country, location),
      image: [featured_image.url],
      about: [category.slug.replace(/\b(\w)/g, (char) => char.toUpperCase())],
      description: replaceText(meta_description, country, location),
      author: author && author.slug ? author.slug.replace(/-/g, ' ').replace(/\b(\w)/g, (char) => char.toUpperCase()) : ''
    };

    const schemaAsString = JSON.stringify(schema, null, 2);

    return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: schemaAsString }} />;
  };

  const renderDefaultSeo = () => {
    const linkTags = [{ rel: 'icon', type: 'image/png', href: SITE_ICON_URL }];
    const languageAlternates = [
      ...generateHreflangTags(location.pathname, baseUrl, manualHreflangs).map(({ hreflangTag, hrefTag }) => ({
        hrefLang: hreflangTag,
        href: hrefTag
      }))
    ];

    return (
      <GatsbySeo
        title={replaceText(seo.title, country, location)}
        description={replaceText(seo.description, country, location)}
        openGraph={seo.openGraph}
        twitter={seo.twitter}
        noindex={handleNoIndex()}
        metaTags={[
          { name: 'viewport', content: 'width=device-width, initial-scale=1, minimum-scale=1, shrink-to-fit=no' },
          { name: 'p:domain_verify', content: 'c4b8e3285b36f353e3e31a766b980e88' },
          { name: 'facebook-domain-verification', content: 'oya3ah1f89sksgi6gd5pg6ahts5yvw' },
          { name: 'google-site-verification', content: 'UPRomG1_5iByI9ghqB75zvrP33dQZO4oWWir4RvIg4s' },
          { name: 'google-site-verification', content: 'TkyyHsfeRD0U9nti6XmuVo38QPcO4Kf3prwksaMXly0' }
          // ... (add other meta tags as needed)
        ]}
        linkTags={linkTags}
        languageAlternates={languageAlternates}
        canonical={canonicalUrl || `${siteUrl}${location.pathname.replace(/\/ads\/$/, '/')}`}
      />
    );
  };

  const renderOrganizationSchema = () => {
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'Daye',
      url: siteUrl,
      logo: DAYE_LOGO_URL
    };

    const schemaAsString = JSON.stringify(schema, null, 2);

    return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: schemaAsString }} />;
  };

  return (
    <>
      {renderDefaultSeo()}
      {renderOrganizationSchema()}
      {productData ? renderProductData() : null}
      {articleData ? renderArticleData() : null}
    </>
  );
};

export default SEOTags;
